import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { scrollToId } from "@/lib/scroll";

export const Hero = () => {
  const { t, lang } = useLanguage();
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const glowX = useSpring(useTransform(mx, [0, 1], ["20%", "80%"]), { stiffness: 60, damping: 20 });
  const glowY = useSpring(useTransform(my, [0, 1], ["25%", "70%"]), { stiffness: 60, damping: 20 });

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="teamup-hero-section"
      onMouseMove={onMouseMove}
      className="relative flex min-h-[100svh] flex-col overflow-hidden"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src="/images/hero.jpg" alt="" className="h-full w-full object-cover object-center opacity-45" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-pitch/70 via-pitch/55 to-pitch" />
      <motion.div
        className="pointer-events-none absolute h-[55vmax] w-[55vmax] rounded-full"
        style={{
          left: glowX,
          top: glowY,
          x: "-50%",
          y: "-50%",
          background: "radial-gradient(circle, rgba(236,93,24,0.12) 0%, transparent 60%)",
        }}
      />
      <div className="noise-overlay" />

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-5 pt-28 pb-16 sm:px-8"
      >
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.7 }}
          className="mb-7 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-brand sm:text-xs"
        >
          <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-brand" />
          {t.hero.eyebrow}
        </motion.p>

        <h1
          data-testid="teamup-hero-headline"
          key={lang}
          className="font-display text-[clamp(2.5rem,8.5vw,7.25rem)] font-extrabold uppercase leading-[0.98] tracking-tight text-white"
        >
          {t.hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-[0.08em]">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.14, duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
                className={`block ${i === 1 ? "text-brand" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-mist sm:text-lg"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            type="button"
            data-testid="teamup-hero-download-cta"
            onClick={() => scrollToId("download")}
            className="group flex items-center gap-2.5 rounded-full bg-brand px-8 py-4 text-base font-bold text-white transition-all duration-300 hover:scale-[1.04] hover:shadow-[0_0_45px_rgba(236,93,24,0.4)] active:scale-95"
          >
            {t.hero.primary}
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            type="button"
            data-testid="teamup-hero-learn-more-cta"
            onClick={() => scrollToId("about")}
            className="rounded-full border border-white/20 px-8 py-4 text-base font-semibold text-white transition-colors duration-300 hover:border-brand/60 hover:text-brand"
          >
            {t.hero.secondary}
          </button>
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">{t.hero.caption}</span>
        </motion.div>
      </motion.div>

      <motion.button
        type="button"
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        className="relative z-10 mx-auto mb-8 flex flex-col items-center gap-2 text-white/40 transition-colors duration-300 hover:text-brand"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown className="h-4 w-4" />
        </motion.span>
      </motion.button>
    </section>
  );
};
